'use client'

import { useEffect } from 'react'
import { useChromeless } from './useChromeless'

// Top offset that clears the fixed header.
//
// The padding is `--pc-content-top` (globals.css): the banner-less header's
// height as a static floor (7.5rem below md, where the header is two rows for
// everyone; 4rem from md up), or the header's measured height when that is
// larger. The floor matches the real header, so the first paint is already
// right and nothing jumps when auth resolves; only a banner, which grows the
// header, moves the page down.
//
// (The floors used to be 9rem and 6rem, written when the header was taller:
// every page then started under a 27-36px empty band. Measured 12 Oct 2026.)
export default function ContentOffset({ children }) {
  const chromeless = useChromeless()

  // Promo/status banners render INSIDE the fixed header, growing it past the
  // floor and overlaying the top of the page. Measure the real <header> (same
  // approach as /generate's CreateSimplified) and react to banner
  // mounts/dismissals and resizes. Published on the root, where globals.css
  // turns it into `--pc-content-top` for /chat's 100dvh panes too.
  useEffect(() => {
    if (chromeless) return undefined
    const root = document.documentElement
    const measure = () => {
      const header = document.querySelector('header')
      const height = header
        ? Math.round(header.getBoundingClientRect().height)
        : 0
      if (height) root.style.setProperty('--pc-header-h', `${height}px`)
      else root.style.removeProperty('--pc-header-h')
    }
    measure()
    const t = setTimeout(measure, 300) // catch late header / banner mount
    window.addEventListener('resize', measure)
    let ro
    const header = document.querySelector('header')
    if (header && 'ResizeObserver' in window) {
      ro = new ResizeObserver(measure)
      ro.observe(header)
    }
    return () => {
      clearTimeout(t)
      window.removeEventListener('resize', measure)
      ro?.disconnect()
      root.style.removeProperty('--pc-header-h')
    }
  }, [chromeless])

  // No header on the Telegram routes, so there is nothing to clear — the
  // offset would otherwise leave blank space above the grid, which is most of
  // the first screen on a phone.
  if (chromeless) {
    return <div className="flex-grow">{children}</div>
  }

  return <div className="flex-grow pt-[var(--pc-content-top)]">{children}</div>
}
